import { animationTransition } from '@shared/utils/animationTransition';
import { Box, Flex, styled, StyleSystemProps } from '@withjoy/joykit';
import { MAP_CARD_POSITIONS } from './MapPin';
import { defaultTheme, pxToRem } from '@withjoy/joykit/theme';
import { LARGE_IMAGE_BORDER_WIDTH, GLASS_BORDER_COLOR, STANDARD_BORDER_COLOR } from './MapPin.constants';

export const CARD_DIMENSIONS = { width: 259, height: 324 };
export const CARD_DIMENSIONS_SAFE = { width: 340, height: 420 };
export const MARKER_DIMENSIONS = { width: 75, height: 50 };

export const StyledMapPinLabelContainer = styled(Box)<{ isAnimate?: boolean }>`
  @keyframes BubbleOpacity {
    0% {
      opacity: 0;
    }
    70% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes BubbleTransform {
    0% {
      transform: scale(0);
    }
    70% {
      transform: scale(1.3);
    }
    100% {
      transform: scale(1);
    }
  }

  ${props =>
    props.isAnimate &&
    `
    opacity: 0;
    transform: scale(0);
    animation: BubbleOpacity 0.7s ease-in-out forwards, BubbleTransform 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  `}
`;
const joyMapPinLabelContainerStyle = ({
  isActive,
  isClickable,
  isCardVisible,
  showOnTop,
}: {
  isActive?: boolean;
  isClickable?: boolean;
  isCardVisible?: boolean;
  showOnTop?: boolean;
}): StyleSystemProps => ({
  zIndex: showOnTop ? 'popover' : 'auto',
  position: 'relative',
  cursor: 'default',
  transition: animationTransition('transform'),
  _hover: {
    ...((isClickable || isActive) && {
      cursor: 'pointer',
      transform: 'scale(1.1)',
      zIndex: 'popover',
      ['.map-pin-image']: { display: 'block' },
    }),
  },
  ...((isActive || isCardVisible) && { transform: 'scale(1.1)', ['.map-pin-image']: { display: 'block' } }),
});

export const StyledMapPinLabelTipShadow = styled(Box)``;
const joyMapPinLabelTipShadowStyle: StyleSystemProps = {
  width: 0,
  height: 0,
  border: '12px solid',
  borderColor: 'white transparent transparent transparent',
  filter: 'drop-shadow(0 1px 5px rgba(0, 0, 0, 0.5))',
  position: 'absolute',
  left: '0',
  bottom: '-12px',
  transform: 'translateX(-50%)',
};

export const StyledMapPinLabel = styled(Flex)``;
const joyMapPinLabelStyle = ({ isActive, isClickable, isCardVisible }: { isActive?: boolean; isClickable?: boolean; isCardVisible?: boolean }): StyleSystemProps => ({
  alignItems: 'center',
  paddingX: 4,
  height: 7,
  color: isActive || isCardVisible ? 'white' : 'typePrimaryDark',
  backgroundColor: isActive || isCardVisible ? 'mono14' : 'white',
  borderRadius: 4,
  width: 'auto',
  position: 'absolute',
  boxShadow: '0 1px 5px -1px rgba(0, 0, 0, 0.5)',
  transform: 'translate(-50%, -40px)',
  whiteSpace: 'nowrap',
  transition: animationTransition('background-color', 'color'),
  _groupHover: {
    ...(isClickable &&
      !isCardVisible &&
      !isActive && {
        backgroundColor: 'fillLightHover',
      }),
  },
});

const joyMapPinWithLabelStyle = ({
  isActive,
  isClickable,
  isCardVisible,
  mapPinColor = 'white',
  mapPinHighlightColor = 'mono14',
  hoverColor = 'fillLightHover',
  transform = 'translate(-50%, -40px)',
  boxShadow = '0 1px 5px -1px rgba(0, 0, 0, 0.5)',
}: {
  isActive?: boolean;
  isClickable?: boolean;
  isCardVisible?: boolean;
  mapPinColor?: string;
  mapPinHighlightColor?: string;
  hoverColor?: string;
  transform?: string;
  boxShadow?: string;
}): StyleSystemProps => ({
  alignItems: 'center',
  padding: 2,
  height: 7,
  color: isActive || isCardVisible ? 'white' : 'typePrimaryDark',
  backgroundColor: isActive || isCardVisible ? mapPinHighlightColor : mapPinColor,
  borderRadius: 4,
  width: 'auto',
  position: 'absolute',
  boxShadow,
  transform,
  whiteSpace: 'nowrap',
  minWidth: pxToRem(64),
  justifyContent: 'center',
  transition: animationTransition('background-color', 'color'),
  _groupHover: {
    ...(isClickable &&
      !isCardVisible &&
      !isActive && {
        backgroundColor: hoverColor,
      }),
  },
});

export const StyledMapPinImage = styled(Box)``;

const joyMapPinImageStyle = ({ image, showImageOnHover, isLargeImage }: { image?: string; showImageOnHover?: boolean; isLargeImage?: boolean }): StyleSystemProps => ({
  width: isLargeImage ? pxToRem(100) : pxToRem(60),
  height: isLargeImage ? pxToRem(100) : pxToRem(60),
  borderRadius: isLargeImage ? pxToRem(8) : pxToRem(5),
  border: isLargeImage ? `${LARGE_IMAGE_BORDER_WIDTH} solid ${GLASS_BORDER_COLOR}` : `1px solid ${STANDARD_BORDER_COLOR}`,
  backgroundColor: 'mono2',
  position: 'absolute',
  bottom: 7,
  left: isLargeImage ? pxToRem(-50) : pxToRem(-29),
  backgroundImage: image,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  display: showImageOnHover ? 'none' : 'block',
});

const joyCircleMapPinLabelStyle = ({
  isActive,
  isClickable,
  isCardVisible,
  isDestination,
}: {
  isActive?: boolean;
  isClickable?: boolean;
  isCardVisible?: boolean;
  isDestination?: boolean;
}): StyleSystemProps => ({
  width: 7,
  height: 7,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  color: isActive || isCardVisible ? 'white' : 'typePrimaryDark',
  paddingTop: isDestination ? '2px' : 0,
  backgroundColor: isDestination ? 'accentBlue4' : isActive || isCardVisible ? 'mono14' : 'white',
  position: 'absolute',
  boxShadow: '0px 5.777px 22.283px 0px rgba(44, 41, 37, 0.06), 0px 3.301px 13.205px 0px rgba(0, 0, 0, 0.07)',
  transform: 'translate(-50%, -20px)',
  whiteSpace: 'nowrap',
  transition: animationTransition('background-color', 'color'),
  _groupHover: {
    ...(isClickable &&
      !isCardVisible &&
      !isActive && {
        backgroundColor: 'fillLightHover',
      }),
  },
});

const joySquareMapPinLabelStyle = ({
  isActive,
  isClickable,
  isCardVisible,
}: {
  isActive?: boolean;
  isClickable?: boolean;
  isCardVisible?: boolean;
  isDestination?: boolean;
}): StyleSystemProps => ({
  width: pxToRem(50),
  height: pxToRem(50),
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: pxToRem(10),
  border: `${isActive && !isCardVisible ? '2px' : '5px'} solid ${isActive || isCardVisible ? '#4951EF' : 'white'}`,
  paddingTop: 0,
  backgroundColor: 'white',
  position: 'absolute',
  boxShadow: '0px 4px 12px -2px rgba(44, 41, 37, 0.25), 0px 3px 7px -3px rgba(0, 0, 0, 0.30)',
  transform: 'translate(-50%, -20px)',
  whiteSpace: 'nowrap',
  transition: animationTransition('background-color', 'color'),
  _groupHover: {
    ...(isClickable &&
      !isCardVisible &&
      !isActive && {
        backgroundColor: 'fillLightHover',
      }),
  },
});

export const StyledMapPinLabelTip = styled(Box)``;
const joyMapPinLabelTipStyle = ({
  isActive,
  isClickable,
  isCardVisible,
  mapPinColor = 'white',
  mapPinHighlightColor = 'mono14',
  hoverColor = 'fillLightHover',
  transform = 'translateX(-50%)',
}: {
  isActive?: boolean;
  isClickable?: boolean;
  isCardVisible?: boolean;
  mapPinHighlightColor?: keyof typeof defaultTheme.colors;
  mapPinColor?: keyof typeof defaultTheme.colors;
  hoverColor?: keyof typeof defaultTheme.colors;
  transform?: string;
}): StyleSystemProps => ({
  width: 0,
  height: 0,
  border: '12px solid',

  borderColor:
    isActive || isCardVisible
      ? `${defaultTheme.colors[mapPinHighlightColor]} transparent transparent transparent`
      : `${defaultTheme.colors[mapPinColor]} transparent transparent transparent`,
  position: 'absolute',
  left: '0',
  bottom: '-12px',
  transform,
  transition: animationTransition('border-color'),
  _groupHover: {
    ...(isClickable &&
      !isCardVisible &&
      !isActive && {
        borderColor: `${defaultTheme.colors[hoverColor]} transparent transparent transparent`,
      }),
  },
});

const _createCardStyle = (usePortal?: boolean, cardPosition?: (typeof MAP_CARD_POSITIONS)[keyof typeof MAP_CARD_POSITIONS], isCardVisible?: boolean) => {
  if (usePortal) {
    return {
      bottom: 'unset',
      transform: 'unset',
    };
  }

  switch (cardPosition) {
    case MAP_CARD_POSITIONS.TOP_CENTER:
      return {
        left: `calc(50% - ${CARD_DIMENSIONS.width / 2}px)`,
        bottom: isCardVisible ? 60 : 20,
      };
    case MAP_CARD_POSITIONS.TOP_RIGHT:
      return {
        left: -(MARKER_DIMENSIONS.width / 2),
        bottom: isCardVisible ? 60 : 20,
      };
    case MAP_CARD_POSITIONS.TOP_LEFT:
      return {
        left: -(CARD_DIMENSIONS.width - MARKER_DIMENSIONS.width / 2),
        bottom: isCardVisible ? 60 : 20,
      };
    case MAP_CARD_POSITIONS.BOTTOM_CENTER:
      return {
        left: `calc(50% - ${CARD_DIMENSIONS.width / 2}px)`,
        bottom: isCardVisible ? -(CARD_DIMENSIONS.height + 20) : -(CARD_DIMENSIONS.height - 20),
      };
    case MAP_CARD_POSITIONS.BOTTOM_RIGHT:
      return {
        left: -(MARKER_DIMENSIONS.width / 2),
        bottom: isCardVisible ? -(CARD_DIMENSIONS.height + 20) : -(CARD_DIMENSIONS.height - 20),
      };
    case MAP_CARD_POSITIONS.BOTTOM_LEFT:
      return {
        left: -(CARD_DIMENSIONS.width - MARKER_DIMENSIONS.width / 2),
        bottom: isCardVisible ? -(CARD_DIMENSIONS.height + 20) : -(CARD_DIMENSIONS.height - 20),
      };
    default:
      return {};
  }
};

export const StyledMapPinCard = styled(Flex)<{ isCardVisible?: boolean }>`
  & * {
    pointer-events: ${props => (props.isCardVisible ? 'auto' : 'none')};
  }
`;
const joyMapPinCardStyle = ({
  isCardVisible,
  cardStyleOverride,
  usePortal,
  cardPosition,
}: {
  isCardVisible?: boolean;
  cardStyleOverride?: StyleSystemProps;
  usePortal?: boolean;
  cardPosition?: (typeof MAP_CARD_POSITIONS)[keyof typeof MAP_CARD_POSITIONS];
}): StyleSystemProps => {
  const cardStyle = _createCardStyle(usePortal, cardPosition, isCardVisible);

  return {
    flexDirection: 'column',
    position: 'absolute',
    backgroundColor: 'white',
    width: CARD_DIMENSIONS.width,
    borderRadius: 12,
    boxShadow: '0 1px 5px -1px rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
    opacity: isCardVisible ? 1 : 0,
    transition: animationTransition('opacity', 'bottom', 'top', 'left', 'right', 'transform'),
    zIndex: 'tooltip',
    pointerEvents: isCardVisible ? 'auto' : 'none',
    ...cardStyle,
    ...(cardStyleOverride || {}),
  };
};

export const StyledEventPhoto = styled(Box)``;
const joyEventPhotoStyle = (isSmall: boolean): StyleSystemProps => {
  const styles = isSmall
    ? {
        top: pxToRem(5),
        left: pxToRem(17),
        width: pxToRem(30),
        height: pxToRem(30),
      }
    : {
        top: pxToRem(7),
        left: pxToRem(27),
        width: 9,
        height: 9,
      };
  return {
    ...styles,
    position: 'absolute',
    borderRadius: '50%',
    border: `${pxToRem(1.185)} solid ${defaultTheme.colors.gray3}`,
  };
};
const joyEventHeartIconStyle = (isSmall: boolean): StyleSystemProps => {
  const styles = isSmall ? { top: 3, left: pxToRem(20) } : { top: pxToRem(14), left: 7 };
  return { ...styles, position: 'absolute' };
};

export const styles = {
  joyMapPinLabelContainer: joyMapPinLabelContainerStyle,
  joyMapPinImage: joyMapPinImageStyle,
  joyMapPinLabelTipShadow: joyMapPinLabelTipShadowStyle,
  joyMapPinLabel: joyMapPinLabelStyle,
  joyCircleMapPinLabel: joyCircleMapPinLabelStyle,
  joyMapPinWithLabel: joyMapPinWithLabelStyle,
  joySquareMapPinLabel: joySquareMapPinLabelStyle,
  joyMapPinLabelTip: joyMapPinLabelTipStyle,
  joyMapPinCard: joyMapPinCardStyle,
  joyEventPhoto: joyEventPhotoStyle,
  joyEventHeartIcon: joyEventHeartIconStyle,
};
